/**
 * Social Feed Service - LeadFlow V2.0 Module 7
 * API service for social feed operations
 */

import axiosInstance from '../config/axios.config';

const BASE_URL = '/leadflow/feed';

export const socialFeedService = {
    /**
     * Get personalized feed
     */
    async getFeed(filters = {}) {
        try {
            const params = new URLSearchParams();

            // Only add post_type if it's a non-empty string (not null, undefined, empty, or object)
            if (filters.post_type && typeof filters.post_type === 'string' && filters.post_type.trim() !== '') {
                params.append('post_type', filters.post_type);
            }
            if (filters.hashtag) params.append('hashtag', filters.hashtag);
            if (filters.profile_id) params.append('profile_id', filters.profile_id);
            if (filters.sort_by) params.append('sort_by', filters.sort_by);
            if (filters.per_page) params.append('per_page', filters.per_page);
            if (filters.page) params.append('page', filters.page);

            const queryString = params.toString();
            const url = queryString ? `${BASE_URL}?${queryString}` : BASE_URL;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('Error fetching feed:', error);
            // Pass through 403 lending-network block data so the UI can show the right state
            if (error.response?.status === 403) {
                return {
                    success: false,
                    status: 403,
                    code: error.response?.data?.code,
                    message: error.response?.data?.message,
                    reason: error.response?.data?.reason,
                };
            }
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch feed'
            };
        }
    },

    /**
     * Create a new post
     */
    async createPost(postData) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts`, postData);
            return response.data;
        } catch (error) {
            console.error('Error creating post:', error);
            // Handle rate limiting with user-friendly message
            if (error.response?.status === 429) {
                return {
                    success: false,
                    message: 'You are posting too quickly. Please wait a moment and try again.'
                };
            }
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to create post'
            };
        }
    },

    /**
     * Get a single post
     */
    async getPost(postId) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/posts/${postId}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching post:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch post'
            };
        }
    },

    /**
     * Update a post
     */
    async updatePost(postId, updates) {
        try {
            const response = await axiosInstance.put(`${BASE_URL}/posts/${postId}`, updates);
            return response.data;
        } catch (error) {
            console.error('Error updating post:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update post'
            };
        }
    },

    /**
     * Delete a post
     */
    async deletePost(postId) {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/posts/${postId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting post:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete post'
            };
        }
    },

    /**
     * Add reaction to post
     */
    async addReaction(postId, reactionType) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts/${postId}/react`, {
                reaction_type: reactionType
            });
            return response.data;
        } catch (error) {
            console.error('Error adding reaction:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to add reaction'
            };
        }
    },

    /**
     * Remove reaction from post
     */
    async removeReaction(postId, reactionType) {
        try {
            // Send as query param — DELETE body can be stripped by some proxies
            const response = await axiosInstance.delete(`${BASE_URL}/posts/${postId}/react?reaction_type=${encodeURIComponent(reactionType)}`);
            return response.data;
        } catch (error) {
            console.error('Error removing reaction:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to remove reaction'
            };
        }
    },

    /**
     * Get comments for a post
     */
    async getComments(postId) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/posts/${postId}/comments`);
            return response.data;
        } catch (error) {
            console.error('Error fetching comments:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch comments'
            };
        }
    },

    /**
     * Add comment to post
     */
    async addComment(postId, content, parentCommentId = null) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts/${postId}/comments`, {
                content,
                parent_comment_id: parentCommentId
            });
            return response.data;
        } catch (error) {
            console.error('Error adding comment:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to add comment'
            };
        }
    },

    /**
     * Update comment
     */
    async updateComment(commentId, content) {
        try {
            const response = await axiosInstance.put(`${BASE_URL}/comments/${commentId}`, {
                content
            });
            return response.data;
        } catch (error) {
            console.error('Error updating comment:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to update comment'
            };
        }
    },

    /**
     * Delete comment
     */
    async deleteComment(commentId) {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/comments/${commentId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting comment:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to delete comment'
            };
        }
    },

    /**
     * Share a post (external share - copy link, etc.)
     */
    async sharePost(postId, comment = null) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts/${postId}/share`, {
                comment
            });
            return response.data;
        } catch (error) {
            console.error('Error sharing post:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to share post'
            };
        }
    },

    /**
     * Repost a post to your feed
     * Uses the share endpoint but specifically for reposting to feed
     */
    async repostPost(postId, quote = null) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts/${postId}/share`, {
                comment: quote,
                is_repost: true
            });
            return response.data;
        } catch (error) {
            console.error('Error reposting:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to repost'
            };
        }
    },

    /**
     * Undo a repost
     */
    async undoRepost(postId) {
        try {
            const response = await axiosInstance.delete(`${BASE_URL}/posts/${postId}/share`);
            return response.data;
        } catch (error) {
            console.error('Error undoing repost:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to undo repost'
            };
        }
    },

    /**
     * Save/unsave a post
     */
    async toggleSave(postId, save) {
        try {
            const response = await axiosInstance.post(`${BASE_URL}/posts/${postId}/save`, {
                save
            });
            return response.data;
        } catch (error) {
            console.error('Error toggling save:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to save post'
            };
        }
    },

    /**
     * Get my posts (posts created by current user)
     */
    async getMyPosts(perPage = 20, page = 1) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/my-posts?per_page=${perPage}&page=${page}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching my posts:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch my posts',
                status: error.response?.status
            };
        }
    },

    /**
     * Get all comments authored by a specific profile (Activity → Comments tab)
     */
    async getProfileComments(profileId, perPage = 10, page = 1) {
        try {
            const response = await axiosInstance.get(
                `${BASE_URL}/profiles/${profileId}/comments?per_page=${perPage}&page=${page}`
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching profile comments:', error?.message || 'Unknown error');
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch comments',
            };
        }
    },

    /**
     * Get saved posts
     */
    async getSavedPosts(perPage = 20, page = 1) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/saved?per_page=${perPage}&page=${page}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching saved posts:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch saved posts'
            };
        }
    },

    /**
     * Get AI-powered "People You May Know" suggestions for the feed sidebar.
     */
    async getPeopleSuggestions(limit = 5) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/people-suggestions?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching people suggestions:', error);
            return { success: false, data: [] };
        }
    },

    /**
     * Get trending hashtags
     */
    async getTrendingHashtags(limit = 10) {
        try {
            const response = await axiosInstance.get(`${BASE_URL}/trending?limit=${limit}`);
            return response.data;
        } catch (error) {
            console.error('Error fetching trending hashtags:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to fetch trending hashtags'
            };
        }
    },

    /**
     * Upload media - accepts FormData or array of files
     */
    async uploadMedia(filesOrFormData) {
        try {
            let formData;

            // Check if it's already FormData
            if (filesOrFormData instanceof FormData) {
                formData = filesOrFormData;
            } else {
                // It's an array of files
                formData = new FormData();
                filesOrFormData.forEach((file, index) => {
                    formData.append(`files[${index}]`, file);
                });
            }

            const response = await axiosInstance.post(`${BASE_URL}/media`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            return response.data;
        } catch (error) {
            console.error('Error uploading media:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to upload media',
                moderation_failed: error.response?.data?.moderation_failed || false
            };
        }
    },

    /**
     * Get count of new posts since last viewed
     * Uses localStorage to track last viewed timestamp
     * @returns {Promise} { success: boolean, data: { new_count: number } }
     */
    async getNewPostsCount() {
        try {
            let lastViewed = localStorage.getItem('feed_last_viewed');
            const user = JSON.parse(localStorage.getItem('user') || '{}');

            // Get profile ID from multiple possible locations
            const myProfileId = user.agent_profile?.id ||
                               user.agentProfile?.id ||
                               user.profile?.id ||
                               user.profile_id;

            // Also get user ID for additional comparison
            const myUserId = user.id || user.user_id;

            // Fetch posts from feed - backend already filters by visibility (public/connections/tenant)
            const response = await axiosInstance.get(BASE_URL, {
                params: { per_page: 20 }
            });

            if (response.data?.success) {
                const posts = response.data.data?.data || response.data.data || [];

                // If never viewed feed before, initialize to 24 hours ago
                // This allows recent posts to show as "new" for first-time users
                if (!lastViewed) {
                    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
                    localStorage.setItem('feed_last_viewed', oneDayAgo);
                    lastViewed = oneDayAgo;
                }

                const lastViewedTime = new Date(lastViewed).getTime();

                // Count posts from OTHER users that are newer than last viewed
                // Backend already filters posts by visibility rules:
                // - public: visible to all
                // - connections: visible only to user's connections
                // - tenant: visible only to same tenant users
                const newPosts = posts.filter(post => {
                    const postTime = new Date(post.created_at).getTime();

                    // Skip posts older than last viewed
                    if (postTime <= lastViewedTime) {
                        return false;
                    }

                    // Check multiple possible field names for author identification
                    const postAuthorProfileId = post.author_profile_id ||
                                               post.author_profile?.id ||
                                               post.profile_id ||
                                               post.author?.profile_id ||
                                               post.author?.id;

                    const postAuthorUserId = post.user_id ||
                                            post.author_user_id ||
                                            post.author?.user_id;

                    // Check if post is from current user (by profile ID or user ID)
                    const isOwnPostByProfile = myProfileId && String(postAuthorProfileId) === String(myProfileId);
                    const isOwnPostByUser = myUserId && String(postAuthorUserId) === String(myUserId);

                    // Post is from others if it's NOT from current user
                    const isFromOthers = !isOwnPostByProfile && !isOwnPostByUser;

                    return isFromOthers;
                });

                return { success: true, data: { new_count: newPosts.length } };
            }

            return { success: false, data: { new_count: 0 } };
        } catch (error) {
            // Silently fail for rate limiting errors to not block UI
            if (error.response?.status === 429) {
                console.debug('Rate limited on feed count check');
            } else {
                console.error('Error fetching new posts count:', error);
            }
            return { success: false, data: { new_count: 0 } };
        }
    },

    /**
     * Mark feed as viewed (updates last viewed timestamp)
     * Uses current time - posts created after this point will show as "new"
     */
    markFeedAsViewed() {
        const timestamp = new Date().toISOString();
        localStorage.setItem('feed_last_viewed', timestamp);
    }
};

export default socialFeedService;
